/**
 * Page subnav — DOM wiring (Kyle, 2026-07-29).
 *
 * The bar STICKS with no JavaScript at all: section-eg-library.css pins it to the
 * top of the viewport and steps it down off the header's own published classes.
 * This file adds two behaviours, and the page is usable without either:
 *
 *   1. the bottom rule fading in and out with scroll DISTANCE;
 *   2. a jump landing the target section flush against the bottom of the bar.
 *
 * All the arithmetic lives in eg-subnav-state.js, which is a pure module with unit
 * tests — the numbers are the part that fails invisibly (a jump that lands 92px
 * low still looks like a working link). What is left here is measuring the page
 * and handing those numbers over.
 *
 * HOW "STUCK" IS MEASURED. `getBoundingClientRect()` cannot tell you: once pinned,
 * a sticky element's rect.top just equals its `top` offset and stops changing, so
 * there is no growing number to fade against. What is needed is the gap between
 * where the bar IS and where it WOULD sit in flow, and the flow position is what
 * `offsetTop` reports — sticky shifts are not folded into it.
 */
import { FADE_DISTANCE, jumpTarget, ruleProgress } from './eg-subnav-state.js';

(function () {
  const SELECTOR = '.eg-subnav-sticky';
  const PROP = '--eg-subnav-rule';

  const bars = () => Array.from(document.querySelectorAll(SELECTOR));
  if (bars().length === 0) return;

  // The bar a jump measures itself against. A page carries one subnav; were there
  // ever two, the first in document order is the one pinned to the top.
  const jumpBar = () => bars()[0];

  /** px value of a CSS custom property on :root, 0 when unset. */
  const rootPx = (name) =>
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;

  /**
   * Distance from the top of the document to an element's RESTING position.
   *
   * Walks the offsetParent chain rather than using rect.top + scrollY, because the
   * latter reports where a stuck element currently is — the very thing being
   * measured against, which would read as permanently zero.
   */
  function restingTop(el) {
    let y = 0;
    for (let node = el; node; node = node.offsetParent) y += node.offsetTop;
    return y;
  }

  // Resting positions only change on layout, so they are cached and invalidated
  // rather than recomputed per frame (each read forces a reflow).
  let resting = new WeakMap();
  const measure = () => {
    resting = new WeakMap();
    for (const bar of bars()) {
      resting.set(bar, restingTop(bar));
      // Published so the CSS fallback offset is the bar's REAL height rather than
      // a number typed in a stylesheet that stops being true when the row wraps
      // or the mono face loads at a different size.
      document.documentElement.style.setProperty(
        '--eg-subnav-height',
        `${bar.offsetHeight}px`,
      );
    }
  };

  function paint() {
    for (const bar of bars()) {
      if (!resting.has(bar)) resting.set(bar, restingTop(bar));
      bar.style.setProperty(
        PROP,
        String(
          ruleProgress({
            scrollY: window.scrollY,
            // 0 while the header is parked, --header-height once revealed, with
            // real intermediate values mid-slide.
            topOffset: parseFloat(getComputedStyle(bar).top) || 0,
            restingTop: resting.get(bar),
            fade: FADE_DISTANCE,
          }),
        ),
      );
    }
  }

  // One paint per frame at most. A scroll handler that touches layout on every
  // event is how a sticky bar starts to feel heavy on a phone.
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      paint();
    });
  };

  /* ------------------------------------------------------------------ jumping
   *
   * A plain `scroll-margin-top` cannot land these, because the chrome above a
   * landed section is not a constant: jumping down hides the header (44px of
   * chrome), jumping up reveals it (145px). One static value is wrong in one
   * direction or the other — 136px left a 92px strip of the previous section on
   * every downward jump. jumpTarget() picks per jump so ONE rule holds both ways.
   *
   * The SECTION element is measured, not the anchor span, so this stays correct if
   * a section ever grows padding above its anchor.
   */
  function jumpTo(id, behavior) {
    const anchor = document.getElementById(id);
    if (!anchor) return false; // unknown target: leave the browser to it

    const section = anchor.closest('.shopify-section') || anchor;
    window.scrollTo({
      top: jumpTarget({
        sectionTop: section.getBoundingClientRect().top + window.scrollY,
        scrollY: window.scrollY,
        headerHeight: rootPx('--header-height'),
        barHeight: jumpBar().offsetHeight,
      }),
      behavior: behavior || 'auto',
    });
    return true;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('.eg-subnav__link');
    if (!link || !link.hash) return;

    const id = decodeURIComponent(link.hash.slice(1));
    if (!document.getElementById(id)) return; // never swallow a click we cannot honour

    event.preventDefault();
    jumpTo(id);

    // The hash still belongs in the URL — these links are shareable, and the back
    // button should undo a jump.
    if (history.pushState) history.pushState(null, '', link.hash);
  });

  // A DEEP LINK gets the same treatment. /pages/booth#podcast arrives with the
  // browser having already applied scroll-margin-top, which is the generous no-JS
  // fallback and lands low; re-running the jump makes it flush.
  const jumpToHash = (behavior) => {
    if (!location.hash || location.hash.length < 2) return;
    jumpTo(decodeURIComponent(location.hash.slice(1)), behavior);
  };

  addEventListener('hashchange', () => jumpToHash());

  measure();
  paint();

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', () => {
    measure();
    schedule();
  });

  // Fonts and lazy images landing above the bar move its resting position, and a
  // stale reading would fade the rule at the wrong point on the page.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      measure();
      schedule();
    });
  }
  addEventListener('load', () => {
    measure();
    schedule();
    // Late images above the target move it, so a deep link is re-landed once the
    // page has stopped growing rather than at DOMContentLoaded.
    jumpToHash('auto');
  });

  // The theme editor re-renders sections in place, replacing what was measured.
  document.addEventListener('shopify:section:load', () => {
    measure();
    schedule();
  });
})();
